import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Select, MenuItem, FormControl, Box, Typography, ListItemIcon, ListItemText
} from '@mui/material';
import DomainIcon from '@mui/icons-material/Domain';
import { fetchMyOrganizations, switchOrganization } from '../../store/organizationsSlice';

/**
 * Org switcher (Phase 1A, ADR 0004) — deliberately renders NOTHING unless the
 * signed-in user actually has 2+ active org memberships. Every real customer
 * today has exactly one org, and the fetch itself silently no-ops for a
 * non-SSO session (see organizationsSlice's own doc comment) - a dropdown
 * that's visible-but-empty for ~100% of current users would be pure UI
 * clutter, not a feature anyone can use yet.
 */
export default function OrgSwitcher() {
  const dispatch = useDispatch();
  const { isAuthenticated, token } = useSelector((s) => s.auth);
  const { list, currentOrgId, switching } = useSelector((s) => s.organizations);

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(fetchMyOrganizations());
  }, [dispatch, isAuthenticated, token]);

  if (!Array.isArray(list) || list.length < 2) return null;

  const currentId = currentOrgId || list[0].organization_id;

  const handleChange = (e) => {
    const targetId = e.target.value;
    if (targetId === currentId) return;
    dispatch(switchOrganization(targetId));
  };

  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <Select
        value={currentId}
        onChange={handleChange}
        disabled={switching}
        displayEmpty
        renderValue={(val) => {
          const org = list.find((o) => o.organization_id === val);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <DomainIcon sx={{ fontSize: 18, color: 'text.secondary' }} aria-hidden="true" />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {org?.company_name || 'Select organization'}
              </Typography>
            </Box>
          );
        }}
        inputProps={{ 'aria-label': 'Organization' }}
        sx={{ '& .MuiSelect-select': { py: 0.8 } }}
      >
        {list.map((org) => (
          <MenuItem key={org.organization_id} value={org.organization_id}>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <DomainIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </ListItemIcon>
            <ListItemText
              primary={org.company_name}
              secondary={org.role}
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
